import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import {
  AlignLeft,
  Bot,
  Eye,
  FileText,
  Loader2,
  Network,
  Pencil,
  Plus,
  Save,
  Settings,
  Shield,
  Check,
  Sparkles,
  Variable,
  Wand2,
  X,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'
import { useConfirmDialog } from '../../ui/ConfirmDialog.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import type { PickerAgentItem } from './PromptAgentPickerDrawer.js'
import { GuardrailPicker } from './GuardrailPicker.js'
import Select from '../../ui/Select.js'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface PromptFormData {
  id?: string
  name: string
  slug: string
  description: string
  category: 'orchestrator' | 'agent' | 'routing' | 'summary' | 'safety'
  content: string
  notes: string
  currentVersion: number
  updatedAt: string
  variables: string[]
}

export interface PromptLinkedAgent {
  id: string
  slug: string
  name: string
  type: string
}

export interface PromptFormDrawerProps {
  initial: PromptFormData | null
  linkedAgents?: PromptLinkedAgent[]
  /** All available agents for assignment selection */
  availableAgents?: PickerAgentItem[]
  /** Currently assigned agent IDs (for edit mode pre-selection) */
  assignedAgentIds?: string[]
  /** Guardrail IDs currently attached to this prompt (edit mode) */
  attachedGuardrailIds?: string[]
  /**
   * agentId → name of a DIFFERENT prompt template the agent already uses.
   * An agent holds one `prompt_id`, so assigning it here replaces that link
   * and overwrites its system prompt text — used to warn before saving.
   */
  otherPromptByAgentId?: Record<string, string>
  isOpen: boolean
  onClose: () => void
  onSave: (data: PromptFormData, selectedAgentIds: string[], selectedGuardrailIds: string[]) => void | Promise<void>
}

// -----------------------------------------------------------------------------
// Theme
// -----------------------------------------------------------------------------

type Category = PromptFormData['category']

const CATEGORY_META: Record<
  Category,
  { label: string; icon: JSX.Element; bg: string; text: string; chip: string }
> = {
  agent: {
    label: 'Agent',
    icon: <Bot className="h-3.5 w-3.5" />,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    chip: 'bg-blue-100 text-blue-800',
  },
  orchestrator: {
    label: 'Orchestrator',
    icon: <Network className="h-3.5 w-3.5" />,
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    chip: 'bg-orange-100 text-orange-800',
  },
  routing: {
    label: 'Routing',
    icon: <Network className="h-3.5 w-3.5" />,
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    chip: 'bg-indigo-100 text-indigo-800',
  },
  summary: {
    label: 'Summary',
    icon: <AlignLeft className="h-3.5 w-3.5" />,
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    chip: 'bg-purple-100 text-purple-800',
  },
  safety: {
    label: 'Safety',
    icon: <Shield className="h-3.5 w-3.5" />,
    bg: 'bg-red-50',
    text: 'text-red-700',
    chip: 'bg-red-100 text-red-800',
  },
}

const SLUG_REGEX = /^[a-z0-9-]+$/
const SLUG_MAX = 100

const inputClass =
  'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'
const labelClass = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'
const errorInputClass =
  'w-full rounded-sm border border-red-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500'

function normalizeSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, SLUG_MAX)
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function extractVariables(content: string): string[] {
  const matches = content.match(/\{\{([a-zA-Z0-9_]+)\}\}/g)
  if (!matches) return []
  return Array.from(new Set(matches.map((m) => m.replace(/[{}]/g, ''))))
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function emptyFormData(): PromptFormData {
  return {
    name: '',
    slug: '',
    description: '',
    category: 'agent',
    content: '',
    notes: '',
    currentVersion: 1,
    updatedAt: new Date().toISOString(),
    variables: [],
  }
}

// -----------------------------------------------------------------------------
// AI generate / polish
// -----------------------------------------------------------------------------

type GenerateTone = 'professional' | 'friendly' | 'concise' | 'detailed'

interface GenerateBrief {
  task: string
  audience: string
  tone: GenerateTone
  category: Category
  constraints: string
  // Structured fields (shared with orchestrator/agent generation)
  goal: string
  rolePersona: string
  instructions: string
}

function emptyBrief(category: Category): GenerateBrief {
  return { task: '', audience: '', tone: 'professional', category, constraints: '', goal: '', rolePersona: '', instructions: '' }
}

interface AiResult {
  content: string
}

const TONE_OPTIONS: { value: GenerateTone; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'concise', label: 'Concise' },
  { value: 'detailed', label: 'Detailed' },
]

// -----------------------------------------------------------------------------
// Focus trap
// -----------------------------------------------------------------------------

function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onEscape: () => void,
) {
  // Only focus the first element when the drawer opens, not on every re-render
  useEffect(() => {
    if (!isOpen) return
    const drawer = ref.current
    if (!drawer) return

    const focusables = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'))

    const first = focusables()[0]
    first?.focus()
  }, [isOpen])

  // Keydown handler — stable, only depends on isOpen and onEscape
  useEffect(() => {
    if (!isOpen) return
    const drawer = ref.current
    if (!drawer) return

    const focusables = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'))

    function handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onEscape()
        return
      }
      if (e.key !== 'Tab' || !drawer) return
      const list = focusables()
      if (list.length === 0) return
      const firstEl = list[0]!
      const lastEl = list[list.length - 1]!
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
  }, [isOpen, onEscape, ref])
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export default function PromptFormDrawer({
  initial,
  attachedGuardrailIds = [],
  isOpen,
  onClose,
  onSave,
}: PromptFormDrawerProps): JSX.Element | null {
  const drawerRef = useRef<HTMLDivElement>(null)
  const { confirm, Dialog, isOpen: isConfirmOpen } = useConfirmDialog()
  const toast = useToast()
  const [form, setForm] = useState<PromptFormData>(() => emptyFormData())
  const [initialState, setInitialState] = useState<PromptFormData>(() => emptyFormData())
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [slugTouched, setSlugTouched] = useState(false)

  // AI generate / polish state
  const [showGenerate, setShowGenerate] = useState(false)
  const [brief, setBrief] = useState<GenerateBrief>(() => emptyBrief('agent'))
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPolishing, setIsPolishing] = useState(false)
  const [polishPreview, setPolishPreview] = useState<{ before: string; after: string } | null>(null)
  // Guardrails selected for inclusion when generating a prompt with AI.
  // The picker resolves selected guardrail IDs to their content strings and
  // passes them as `guardrailContents` to the generate-prompt API.
  const [selectedGuardrailIds, setSelectedGuardrailIds] = useState<Set<string>>(new Set())
  const [selectedGuardrailContents, setSelectedGuardrailContents] = useState<string[]>([])

  // Variable preview state
  const [showPreview, setShowPreview] = useState(false)
  const [previewVars, setPreviewVars] = useState<Record<string, string>>({})
  const [previewResult, setPreviewResult] = useState<{ rendered: string; missingVariables: string[] } | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)

  const isEdit = !!initial?.id
  const [activeTab, setActiveTab] = useState<'basic' | 'prompt'>('basic')

  const PROMPT_TAB_ORDER = ['basic', 'prompt'] as const

  useEffect(() => {
    if (isOpen) {
      const next = initial ? deepClone(initial) : emptyFormData()
      setForm(next)
      setInitialState(next)
      setIsDirty(false)
      setShowErrors(false)
      setSlugTouched(!!initial?.id)
      setBrief(emptyBrief(initial?.category ?? 'agent'))
      setShowGenerate(false)
      setShowPreview(false)
      setSelectedGuardrailIds(new Set(attachedGuardrailIds))
      setSelectedGuardrailContents([])
      setPreviewVars({})
      setPreviewResult(null)
      setActiveTab('basic')
    }
  }, [isOpen, initial])

  // Auto-slug from name in create mode until user edits slug
  useEffect(() => {
    if (isOpen && !isEdit && form.name && !slugTouched) {
      setForm((prev) => ({ ...prev, slug: normalizeSlug(form.name) }))
    }
  }, [isOpen, isEdit, form.name, slugTouched])

  useEffect(() => {
    setIsDirty(JSON.stringify(form) !== JSON.stringify(initialState))
  }, [form, initialState])

  const slugError = useMemo(() => {
    const s = form.slug.trim()
    if (!s) return 'Slug is required'
    if (!SLUG_REGEX.test(s)) return 'Only lowercase letters, numbers, and hyphens are allowed'
    return ''
  }, [form.slug])

  const nameError = showErrors && !form.name.trim() ? 'Name is required' : ''
  const contentError = showErrors && !form.content.trim() ? 'Prompt template is required' : ''
  const canSave =
    isDirty && !nameError && !slugError && !contentError && form.name.trim() && form.slug.trim() && form.content.trim()

  const update = useCallback((patch: Partial<PromptFormData>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }, [])

  const handleClose = useCallback(() => {
    if (isDirty) {
      confirm({
        title: 'Discard Changes',
        message: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep Editing',
        onConfirm: () => onClose(),
      })
      return
    }
    onClose()
  }, [isDirty, confirm, onClose])

  const persist = async () => {
    setIsSaving(true)
    try {
      await onSave(form, [], Array.from(selectedGuardrailIds))
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = () => {
    setShowErrors(true)
    if (!canSave) return
    void persist()
  }

  const handleGenerate = async () => {
    if (!brief.task.trim()) {
      toast.error('Task is required', 'Describe what the prompt should accomplish.')
      return
    }
    setIsGenerating(true)
    try {
      const res = await api.post<AiResult>('/admin/ai/prompts/generate', {
        task: brief.task.trim(),
        ...(brief.audience.trim() ? { audience: brief.audience.trim() } : {}),
        tone: brief.tone,
        category: brief.category,
        ...(brief.constraints.trim() ? { constraints: brief.constraints.trim() } : {}),
        // Structured fields (shared with orchestrator/agent generation)
        ...(brief.goal.trim() ? { goal: brief.goal.trim() } : {}),
        ...(brief.rolePersona.trim() ? { rolePersona: brief.rolePersona.trim() } : {}),
        ...(brief.instructions.trim() ? { instructions: brief.instructions.trim() } : {}),
        // Guardrails selected in the picker — appended as a mandatory final
        // layer by the generate-prompt API.
        guardrailContents: selectedGuardrailContents,
      })
      update({ content: res.content })
      setShowGenerate(false)
      toast.success('Prompt generated', 'Review and edit the generated content below.')
    } catch (err) {
      toast.error('Generation failed', err instanceof Error ? err.message : String(err))
    } finally {
      setIsGenerating(false)
    }
  }

  const handlePolish = async () => {
    if (!form.content.trim()) {
      toast.error('Nothing to polish', 'Write or generate some prompt content first.')
      return
    }
    setIsPolishing(true)
    try {
      const res = await api.post<AiResult>('/admin/ai/prompts/polish', {
        content: form.content,
        category: form.category,
      })
      setPolishPreview({ before: form.content, after: res.content })
    } catch (err) {
      toast.error('Polish failed', err instanceof Error ? err.message : String(err))
    } finally {
      setIsPolishing(false)
    }
  }

  const handleAcceptPolish = (): void => {
    if (polishPreview) {
      update({ content: polishPreview.after })
      setPolishPreview(null)
      toast.success('Prompt polished', 'The improved content has been applied. Review and save.')
    }
  }

  const handleRejectPolish = (): void => {
    setPolishPreview(null)
  }

  const handlePreview = async () => {
    if (!form.content.trim()) return
    setIsPreviewing(true)
    try {
      const res = await api.post<{ rendered: string; detectedVariables: string[]; missingVariables: string[] }>(
        '/admin/ai/prompts/preview',
        { content: form.content, variables: previewVars },
      )
      setPreviewResult({ rendered: res.rendered, missingVariables: res.missingVariables })
    } catch (err) {
      toast.error('Preview failed', err instanceof Error ? err.message : String(err))
    } finally {
      setIsPreviewing(false)
    }
  }

  // Suspended while a confirmation dialog is up, so Escape/Tab act on the
  // dialog rather than dismissing the whole drawer behind it.
  useFocusTrap(drawerRef, isOpen && !isConfirmOpen, handleClose)

  if (!isOpen) return null

  const detectedVariables = extractVariables(form.content)

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="prompt-form-title">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={handleClose} aria-hidden="true" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl md:w-[640px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex items-center gap-2">
            {isEdit ? <Pencil className="h-4 w-4 text-gray-500" /> : <Plus className="h-4 w-4 text-gray-500" />}
            <span id="prompt-form-title" className="text-sm font-semibold text-gray-900">
              {isEdit ? 'Edit prompt template' : 'New prompt template'}
            </span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close drawer"
            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Edit mode version header */}
        {isEdit && (
          <div className="border-b border-gray-100 bg-gray-50/50 px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="font-semibold text-gray-900">Version {form.currentVersion}</span>
              <span className="text-gray-300">|</span>
              <span>Last edited {formatDate(form.updatedAt)}</span>
            </div>
          </div>
        )}

        {/* Tab navigation */}
        <div className="flex border-b border-gray-200 px-4">
          {([
            { key: 'basic' as const, label: 'Basic', icon: <Settings className="h-3.5 w-3.5" /> },
            { key: 'prompt' as const, label: 'Prompt & Guardrails', icon: <FileText className="h-3.5 w-3.5" /> },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors ${
                activeTab === tab.key
                  ? 'border-[#3454cf] text-[#3454cf]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 space-y-5 overflow-y-auto p-4 pb-28">
          {activeTab === 'basic' && (
          <div className="space-y-5">
          {/* Name */}
          <div>
            <label htmlFor="prompt-name" className={labelClass}>
              Name *
            </label>
            <input
              id="prompt-name"
              type="text"
              value={form.name}
              onChange={(e) => {
                const name = e.target.value
                const patch: Partial<PromptFormData> = { name }
                if (isOpen && !isEdit && !slugTouched) {
                  patch.slug = normalizeSlug(name)
                }
                update(patch)
              }}
              placeholder="e.g. Tier-1 Support System Prompt"
              className={nameError ? errorInputClass : inputClass}
              aria-invalid={!!nameError}
            />
            {nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="prompt-slug" className={labelClass}>
              Slug *
            </label>
            <input
              id="prompt-slug"
              type="text"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true)
                update({ slug: normalizeSlug(e.target.value) })
              }}
              onBlur={() => setSlugTouched(true)}
              disabled={isEdit}
              placeholder="tier-1-support-prompt"
              className={`${isEdit ? 'cursor-not-allowed bg-gray-50 text-gray-500 ' : ''}${slugError ? errorInputClass : inputClass}`}
              aria-invalid={!!slugError}
            />
            {slugError ? (
              <p className="mt-1 text-xs text-red-500">{slugError}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-400">Used in API calls and logs</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label htmlFor="prompt-category" className={labelClass}>
              Category *
            </label>
            <div className="relative">
              <Select
                ariaLabel="Category"
                value={form.category}
                onChange={(v) => update({ category: v as Category })}
              >
                {(['agent', 'orchestrator', 'routing', 'summary', 'safety'] as const).map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_META[c].label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="prompt-description" className={labelClass}>
              Description
            </label>
            <input
              id="prompt-description"
              type="text"
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="Brief context on where this prompt is used..."
              className={inputClass}
            />
          </div>
          </div>
          )}

          {activeTab === 'prompt' && (
          <div className="space-y-5">

          {/* Guardrail selection — available whether the prompt is written
              manually or generated with AI. Selected guardrails are passed
              as `guardrailContents` to the generate-prompt API when AI
              generation is used. */}
          <GuardrailPicker
            selectedIds={selectedGuardrailIds}
            onChange={(ids, contents) => {
              setSelectedGuardrailIds(ids)
              setSelectedGuardrailContents(contents)
            }}
          />

          {/* Prompt template */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label htmlFor="prompt-content" className={labelClass}>
                Prompt template *
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setBrief((prev) => ({ ...prev, category: form.category }))
                    setShowGenerate((prev) => !prev)
                  }}
                  className="inline-flex items-center gap-1 rounded-sm border border-[#3454cf]/30 bg-[#3454cf]/5 px-2 py-1 text-[10px] font-semibold text-[#3454cf] transition-colors hover:bg-[#3454cf]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3454cf]/40 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Sparkles className="h-3 w-3" />
                  Generate with AI
                </button>
                <button
                  type="button"
                  onClick={() => void handlePolish()}
                  disabled={isPolishing || !form.content.trim()}
                  className="inline-flex items-center gap-1 rounded-sm border border-purple-300 bg-purple-50 px-2 py-1 text-[10px] font-semibold text-purple-700 transition-colors hover:bg-purple-100 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                >
                  {isPolishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                  Polish
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPreviewVars({})
                    setPreviewResult(null)
                    setShowPreview((prev) => !prev)
                  }}
                  disabled={!form.content.trim()}
                  className="inline-flex items-center gap-1 rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                >
                  <Eye className="h-3 w-3" />
                  Preview
                </button>
              </div>
            </div>

            {/* AI generate panel */}
            {showGenerate && (
              <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>What should this prompt do? *</label>
                    <textarea
                      value={brief.task}
                      onChange={(e) => setBrief((prev) => ({ ...prev, task: e.target.value }))}
                      rows={2}
                      className={`${inputClass} resize-none`}
                      placeholder="e.g. Route customer messages to the right department based on intent and language"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Audience</label>
                      <input
                        type="text"
                        value={brief.audience}
                        onChange={(e) => setBrief((prev) => ({ ...prev, audience: e.target.value }))}
                        className={inputClass}
                        placeholder="e.g. Support agent"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Tone</label>
                      <Select
                        ariaLabel="Tone"
                        value={brief.tone}
                        onChange={(v) => setBrief((prev) => ({ ...prev, tone: v as GenerateTone }))}
                      >
                        {TONE_OPTIONS.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowGenerate(false)}
                      className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleGenerate()}
                      disabled={isGenerating || !brief.task.trim()}
                      className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                    >
                      {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      Generate
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Polish preview — before/after diff with accept/reject */}
            {polishPreview && (
              <div className="mb-3 rounded-sm border border-purple-200 bg-purple-50/40 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-700">Polish preview</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleRejectPolish}
                      className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={handleAcceptPolish}
                      className="inline-flex items-center gap-1 rounded-sm bg-purple-600 px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-purple-700"
                    >
                      <Check className="h-3 w-3" />
                      Accept
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Before</p>
                    <pre className="max-h-48 overflow-auto rounded-sm bg-white p-2 text-[10px] text-gray-600 whitespace-pre-wrap">{polishPreview.before}</pre>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-purple-600">After</p>
                    <pre className="max-h-48 overflow-auto rounded-sm bg-white p-2 text-[10px] text-gray-900 whitespace-pre-wrap">{polishPreview.after}</pre>
                  </div>
                </div>
              </div>
            )}

            <textarea
              id="prompt-content"
              value={form.content}
              onChange={(e) => update({ content: e.target.value })}
              rows={8}
              className={`${contentError ? errorInputClass : inputClass} resize-none font-mono`}
              style={{ minHeight: '200px' }}
              placeholder="You are an AI support agent for {{companyName}}. Help the customer with..."
            />
            {contentError && <p className="mt-1 text-xs text-red-500">{contentError}</p>}

            {/* Variables */}
            {detectedVariables.length > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-sm border border-blue-200 bg-blue-50/60 p-2.5">
                <Variable className="mt-0.5 h-4 w-4 shrink-0 text-[#3454cf]" />
                <div className="flex flex-wrap gap-1.5">
                  {detectedVariables.map((v) => (
                    <span
                      key={v}
                      className="rounded-sm bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800"
                    >
                      {'{{' + v + '}}'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Variable preview panel */}
            {showPreview && (
              <div className="mt-3 rounded-sm border border-emerald-200 bg-emerald-50/40 p-3">
                <div className="mb-2 flex items-center gap-1.5">
                  <Eye className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-[11px] font-semibold text-emerald-800">Variable Preview</span>
                </div>

                {detectedVariables.length === 0 ? (
                  <p className="text-[11px] text-gray-500">
                    No variables detected. Use <code className="rounded bg-white px-1 font-mono text-[10px]">{'{{variableName}}'}</code> syntax in the prompt to add variables.
                  </p>
                ) : (
                  <>
                    {/* Variable inputs */}
                    <div className="mb-3 space-y-1.5">
                      {detectedVariables.map((v) => (
                        <div key={v} className="flex items-center gap-2">
                          <span className="w-32 shrink-0 font-mono text-[10px] font-medium text-gray-600">{'{{' + v + '}}'}</span>
                          <input
                            type="text"
                            value={previewVars[v] ?? ''}
                            onChange={(e) => setPreviewVars((prev) => ({ ...prev, [v]: e.target.value }))}
                            placeholder={`Value for ${v}...`}
                            className="flex-1 rounded-sm border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Preview action */}
                    <div className="mb-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void handlePreview()}
                        disabled={isPreviewing}
                        className="inline-flex items-center gap-1 rounded-sm bg-emerald-600 px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {isPreviewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                        Render Preview
                      </button>
                      {previewResult && previewResult.missingVariables.length > 0 && (
                        <span className="text-[10px] text-amber-600">
                          {previewResult.missingVariables.length} variable{previewResult.missingVariables.length === 1 ? '' : 's'} not filled
                        </span>
                      )}
                    </div>

                    {/* Rendered output */}
                    {previewResult && (
                      <div>
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Rendered Output</div>
                        <pre className="max-h-48 overflow-y-auto rounded-sm border border-gray-200 bg-white p-2.5 font-mono text-[11px] leading-relaxed text-gray-700 thin-scrollbar whitespace-pre-wrap">
                          {previewResult.rendered}
                        </pre>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          </div>
          )}

          {activeTab === 'basic' && isEdit && (
            <div>
              <label htmlFor="prompt-notes" className={labelClass}>
                Change summary
              </label>
              <input
                id="prompt-notes"
                type="text"
                value={form.notes}
                onChange={(e) => update({ notes: e.target.value })}
                placeholder="What changed in this version?"
                className={inputClass}
              />
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            {/* Left: Back button */}
            {activeTab !== PROMPT_TAB_ORDER[0] ? (
              <button
                type="button"
                onClick={() => {
                  const idx = PROMPT_TAB_ORDER.indexOf(activeTab)
                  if (idx > 0) setActiveTab(PROMPT_TAB_ORDER[idx - 1]!)
                }}
                className="inline-flex items-center gap-1.5 rounded-sm border border-gray-200 px-4 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
            ) : (
              <span />
            )}
            {/* Right: Cancel + Next/Save */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-sm border border-gray-200 px-4 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                Cancel
              </button>
              {activeTab !== PROMPT_TAB_ORDER[PROMPT_TAB_ORDER.length - 1] ? (
                <button
                  type="button"
                  onClick={() => {
                    const idx = PROMPT_TAB_ORDER.indexOf(activeTab)
                    if (idx < PROMPT_TAB_ORDER.length - 1) setActiveTab(PROMPT_TAB_ORDER[idx + 1]!)
                  }}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#3454cf]"
                >
                  Next
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave || isSaving}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#3454cf]"
                >
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  {isEdit ? 'Save' : 'Create prompt'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog />
    </div>
  )
}
